import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableHead,
  Paper,
  Chip,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  Tooltip,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChatIcon from "@mui/icons-material/Chat";
import { getStatusChipProps } from "../../Patient.utils";
import { ActionsBox } from "../../Patient.styles";
import {
  COLUMNS_CONFIG,
  COLUMN_IDS,
  BOOK_FOLLOW_UP_LABEL,
  CANCEL_LABEL,
  APPOINTMENT_TYPE_LABELS,
  CONSULTATION_REQUIRED_TOOLTIP,
} from "./AppointmentsTable.constants";
import {
  StyledTableContainer,
  StyledHeaderRow,
  StyledHeaderCell,
  StyledBodyRow,
  StyledBodyCell,
  StyledBookFollowUpBtn,
  AppointmentCardPaper,
  AppointmentCardGrid,
  MobileListBox,
  DateTimeDateText,
  DateTimeTimeText,
  ChipPaid,
  ChipPast,
  ChipFreeConsultation,
  ChipPaymentPending,
  ChipFollowUpOutlined,
  CardIndexBadge,
} from "./AppointmentsTable.styles";

const DATE_DISPLAY_OPTIONS = {
  weekday: "short",
  month: "short",
  day: "numeric",
};

const TIME_DISPLAY_OPTIONS = {
  hour: "2-digit",
  minute: "2-digit",
  timeZoneName: "short",
};

const formatDateTime = (startTime) => {
  if (!startTime) return "N/A";
  const date = new Date(startTime);
  const dateStr = date.toLocaleDateString("en-US", DATE_DISPLAY_OPTIONS);
  const timeStr = date.toLocaleTimeString("en-US", TIME_DISPLAY_OPTIONS);
  return (
    <Box>
      <DateTimeDateText variant="body2">{dateStr}</DateTimeDateText>
      <DateTimeTimeText variant="caption">{timeStr}</DateTimeTimeText>
    </Box>
  );
};

const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return "N/A";
  const start = new Date(startTime);
  const end = new Date(endTime);
  const minutes = Math.round((end - start) / (1000 * 60));
  return `${minutes} min`;
};

const getPaymentStatusChip = (isPaid, isFreeConsultation, isUpcomingSession) => {

  const label = isPaid ? "Paid" : isFreeConsultation ? "Free Consultation" : "Payment Pending";

  if (!isUpcomingSession) {
    return <ChipPast label={label} size="small" />;
  }
  if (isPaid) {
    return <ChipPaid label={label} size="small" />;
  }
  if (isFreeConsultation) {
    return <ChipFreeConsultation label={label} size="small" />;
  }
  return <ChipPaymentPending label={label} size="small" />;
};

const getAppointmentTypeChip = (isFreeConsultation, isUpcomingSession) => {
  const label = isFreeConsultation
    ? APPOINTMENT_TYPE_LABELS.FREE_CONSULTATION
    : APPOINTMENT_TYPE_LABELS.FOLLOW_UP;

  if (!isUpcomingSession) {
    return (
      <ChipPast
        label={label}
        size="small"
      />
    );
  }
  if (isFreeConsultation) {
    return (
      <ChipFreeConsultation
        label={label}
        size="small"
      />
    );
  }
  return (
    <ChipFollowUpOutlined
      label={label}
      size="small"
      variant="outlined"
    />
  );
};

const getCellContent = (columnId, appt, getPractitionerName, helpers, isUpcomingSession) => {
  switch (columnId) {
    case COLUMN_IDS.CLIENT:
      return appt.contact?.name || appt.title || "N/A";
    case COLUMN_IDS.APPOINTMENT:
      return appt.title;
    case COLUMN_IDS.PRACTITIONER:
      return getPractitionerName(appt.assignedUserId);
    case COLUMN_IDS.STATUS:
      return (
        <Chip {...getStatusChipProps(appt.appointmentStatus, appt.notes, isUpcomingSession)} />
      );
    case COLUMN_IDS.DATE_TIME:
      return formatDateTime(appt.startTime);
    case COLUMN_IDS.DURATION:
      return formatDuration(appt.startTime, appt.endTime);
    case COLUMN_IDS.APPOINTMENT_TYPE: {
      const isFree = helpers?.getIsFreeConsultation?.(appt) ?? false;
      return getAppointmentTypeChip(isFree, isUpcomingSession);
    }
    case COLUMN_IDS.PAYMENT_STATUS: {
      const isPaid = helpers?.getIsPaid?.(appt) ?? false;
      const isFree = helpers?.getIsFreeConsultation?.(appt) ?? false;
      return getPaymentStatusChip(isPaid, isFree, isUpcomingSession);
    }
    default:
      return null;
  }
};

const AppointmentCard = ({
  appt,
  columns,
  isUpcomingSession,
  getPractitionerName,
  helpers,
  onViewDetails,
  onBookFollowUp,
  onReschedule,
  onCancel,
  onMessageClient,
  index,
  consultationCompleted = true,
}) => {
  const isCancelled = appt.appointmentStatus?.toLowerCase() === "cancelled";
  const hasActions = columns.some((col) => col.id === COLUMN_IDS.ACTIONS);
  const dataColumns = columns.filter((col) => col.id !== COLUMN_IDS.ACTIONS);

  return (
    <AppointmentCardPaper
      variant="outlined"
      onClick={() => onViewDetails(appt)}
      isUpcomingSession={isUpcomingSession}
    >
      <CardIndexBadge>{index + 1}</CardIndexBadge>
      <AppointmentCardGrid>
        {dataColumns.map((col) => {
          const content = getCellContent(
            col.id,
            appt,
            getPractitionerName,
            helpers,
            isUpcomingSession
          );
          return (
            <React.Fragment key={col.id}>
              <Typography variant="caption" color="text.secondary">
                {col.label}
              </Typography>
              <Box>{content}</Box>
            </React.Fragment>
          );
        })}
        {hasActions && (
          <>
            <Typography variant="caption" color="text.secondary">
              Actions
            </Typography>
            <ActionsBox onClick={(e) => e.stopPropagation()}>
              {consultationCompleted ? (
                <StyledBookFollowUpBtn
                  variant="outlined"
                  size="small"
                  startIcon={<EventIcon />}
                  onClick={() => onBookFollowUp(appt)}
                >
                  {BOOK_FOLLOW_UP_LABEL}
                </StyledBookFollowUpBtn>
              ) : (
                <Tooltip title={CONSULTATION_REQUIRED_TOOLTIP} arrow>
                  <span>
                    <StyledBookFollowUpBtn
                      variant="outlined"
                      size="small"
                      startIcon={<EventIcon />}
                      disabled
                    >
                      {BOOK_FOLLOW_UP_LABEL}
                    </StyledBookFollowUpBtn>
                  </span>
                </Tooltip>
              )}

              {onCancel && !isCancelled && isUpcomingSession && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => onCancel(appt)}
                  sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
                >
                  {CANCEL_LABEL}
                </Button>
              )}
              {onMessageClient && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ChatIcon />}
                  onClick={() => onMessageClient(appt)}
                  sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
                >
                  Message Client
                </Button>
              )}
            </ActionsBox>
          </>
        )}
      </AppointmentCardGrid>
    </AppointmentCardPaper>
  );
};

const TableView = ({
  variant,
  isUpcomingSession,
  appointments,
  getPractitionerName,
  onViewDetails,
  onBookFollowUp,
  helpers,
  onReschedule,
  onCancel,
  onMessageClient,
  consultationCompleted = true,
}) => {
  const columns = COLUMNS_CONFIG[variant];
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuApptId, setMenuApptId] = useState(null);

  const handleMenuOpen = (event, apptId) => {
    setAnchorEl(event.currentTarget);
    setMenuApptId(apptId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuApptId(null);
  };
  return (
    <StyledTableContainer component={Paper}>
      <Table>
        <TableHead>
          <StyledHeaderRow>
            {columns.map((col) => (
              <StyledHeaderCell key={col.id}>{col.label}</StyledHeaderCell>
            ))}
          </StyledHeaderRow>
        </TableHead>
        <TableBody>
          {appointments.map((appt) => (
            <StyledBodyRow key={appt.id} status={appt.appointmentStatus} isUpcomingSession={isUpcomingSession}>
              {columns.map((col) => {
                if (col.id === COLUMN_IDS.ACTIONS) {
                  const isCancelled =
                    appt.appointmentStatus?.toLowerCase() === "cancelled";
                  return (
                    <StyledBodyCell key={col.id}>
                      <ActionsBox>
                        <Button
                          variant="outlined"
                          size="small"
                          endIcon={<ArrowDropDownIcon />}
                          onClick={(e) => handleMenuOpen(e, appt.id)}
                          sx={{
                            textTransform: "none",
                            fontFamily: "'Gotham', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        >
                          Actions
                        </Button>
                        <Menu
                          anchorEl={anchorEl}
                          open={menuApptId === appt.id}
                          onClose={handleMenuClose}
                        >
                          {consultationCompleted ? (
                            <MenuItem
                              onClick={() => {
                                handleMenuClose();
                                onBookFollowUp(appt);
                              }}
                            >
                              <ListItemIcon>
                                <EventIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>{BOOK_FOLLOW_UP_LABEL}</ListItemText>
                            </MenuItem>
                          ) : (
                            <Tooltip title={CONSULTATION_REQUIRED_TOOLTIP} arrow placement="right">
                              <span>
                                <MenuItem disabled>
                                  <ListItemIcon>
                                    <EventIcon fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText>{BOOK_FOLLOW_UP_LABEL}</ListItemText>
                                </MenuItem>
                              </span>
                            </Tooltip>
                          )}
                          {onCancel && !isCancelled && isUpcomingSession && (
                            <MenuItem
                              onClick={() => {
                                handleMenuClose();
                                onCancel(appt);
                              }}
                            >
                              <ListItemIcon>
                                <CancelIcon fontSize="small" color="error" />
                              </ListItemIcon>
                              <ListItemText sx={{ color: "error.main" }}>
                                {CANCEL_LABEL}
                              </ListItemText>
                            </MenuItem>
                          )}
                          {onMessageClient && (
                            <MenuItem
                              onClick={() => {
                                handleMenuClose();
                                onMessageClient(appt);
                              }}
                            >
                              <ListItemIcon>
                                <ChatIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>Message Client</ListItemText>
                            </MenuItem>
                          )}
                        </Menu>
                      </ActionsBox>
                    </StyledBodyCell>
                  );
                }

                const content = getCellContent(
                  col.id,
                  appt,
                  getPractitionerName,
                  helpers,
                  isUpcomingSession
                );
                const isChipColumn =
                  col.id === COLUMN_IDS.STATUS ||
                  col.id === COLUMN_IDS.PAYMENT_STATUS ||
                  col.id === COLUMN_IDS.APPOINTMENT_TYPE;
                const applyStyles = !isChipColumn;

                return (
                  <StyledBodyCell
                    key={col.id}
                    onClick={() => onViewDetails(appt)}
                    applyStyles={applyStyles}
                    isUpcomingSession={isUpcomingSession}
                  >
                    {content}
                  </StyledBodyCell>
                );
              })}
            </StyledBodyRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

const AppointmentsTable = ({
  variant,
  isUpcomingSession,
  appointments,
  getPractitionerName,
  onViewDetails,
  onBookFollowUp,
  onReschedule,
  onCancel,
  onMessageClient,
  getIsPaid,
  getIsFreeConsultation,
  consultationCompleted = true,
}) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down(1130));
  const columns = COLUMNS_CONFIG[variant];
  const helpers = { getIsPaid, getIsFreeConsultation };

  if (isNarrow) {
    return (
      <MobileListBox>
        {appointments.map((appt, index) => (
          <AppointmentCard
            key={appt.id}
            appt={appt}
            columns={columns}
            getPractitionerName={getPractitionerName}
            helpers={helpers}
            onViewDetails={onViewDetails}
            onBookFollowUp={onBookFollowUp}
            onReschedule={onReschedule}
            onCancel={onCancel}
            onMessageClient={onMessageClient}
            index={index}
            consultationCompleted={consultationCompleted}
            isUpcomingSession={isUpcomingSession}
          />
        ))}
      </MobileListBox>
    );
  }

  return (
    <TableView
      variant={variant}
      appointments={appointments}
      getPractitionerName={getPractitionerName}
      onViewDetails={onViewDetails}
      onBookFollowUp={onBookFollowUp}
      onReschedule={onReschedule}
      onCancel={onCancel}
      onMessageClient={onMessageClient}
      helpers={helpers}
      consultationCompleted={consultationCompleted}
      isUpcomingSession={isUpcomingSession}
    />
  );
};

export default AppointmentsTable;
